import type { PolicyDocument } from '../../types/policy';
import { company as c } from '../company';

const contact = c.mailingAddress ? `${c.privacyEmail} or by mail at ${c.mailingAddress}` : c.privacyEmail;

export const privacyPolicy: PolicyDocument = {
  title: 'Privacy Policy',
  lead: `How ${c.brand} collects, uses, shares and protects your personal information when you visit ${c.website} or buy from us.`,
  sections: [
    {
      id: 'scope',
      heading: '1. Scope',
      blocks: [
        `This Privacy Policy applies to personal information collected by ${c.legalName} through this website, our checkout, customer support, warranty claims, email and SMS communications, and social media advertising. By using our website you acknowledge this Policy.`,
      ],
    },
    {
      id: 'collect',
      heading: '2. Information we collect',
      blocks: [
        'Information you provide:',
        {
          list: [
            'Identifiers and contact details — name, email address, phone number, shipping and billing address.',
            'Order information — products purchased, order value, order history and communications with us.',
            'Payment information — processed directly by our payment providers. We do not store full card numbers.',
            'Support and warranty information — messages, photos and videos you send us.',
            'Reviews and other content you choose to submit.',
          ],
        },
        'Information collected automatically:',
        {
          list: [
            'Device and browser data — IP address, device type, operating system, browser, language and approximate location derived from IP.',
            'Usage data — pages viewed, products viewed, items added to cart, referring website, and time on site.',
            'Cookies, pixels and similar technologies, as described below.',
          ],
        },
      ],
    },
    {
      id: 'use',
      heading: '3. How we use your information',
      blocks: [
        {
          list: [
            'To process, ship and deliver orders, and send order and shipping notifications.',
            'To provide customer support and handle returns and warranty claims.',
            'To detect, prevent and investigate fraud, chargebacks and security incidents.',
            'To send marketing emails and, with your consent, text messages. You can opt out at any time.',
            'To measure and improve our website, products and advertising, including showing you relevant ads on other platforms.',
            'To comply with legal obligations and enforce our Terms of Service.',
          ],
        },
      ],
    },
    {
      id: 'sharing',
      heading: '4. How we share information',
      blocks: [
        'We share personal information only as needed to operate our business:',
        {
          list: [
            'E-commerce platform and payment processors (such as Shopify and its payment partners) to host our store and process payments.',
            'Shipping carriers and fulfillment partners to deliver your order.',
            'Email, SMS and customer-support providers to communicate with you.',
            'Analytics and advertising partners (such as Meta and Google) to measure and deliver advertising.',
            'Professional advisers, and authorities when required by law, to protect our rights, or to prevent fraud.',
            'A buyer or successor in connection with a merger, acquisition or sale of assets.',
          ],
        },
        'We do not sell your personal information for money. Sharing data with advertising partners for cross-context behavioral advertising may be considered a "sale" or "sharing" under some state laws; you can opt out as described below.',
      ],
    },
    {
      id: 'cookies',
      heading: '5. Cookies, pixels and advertising',
      blocks: [
        'We and our partners use cookies, the Meta Pixel and Conversions API, Google tags and similar technologies to keep your cart, remember preferences, understand how visitors use our site, and measure and personalize advertising. These partners may collect information about your activity on our site and other sites over time.',
        'You can control cookies through your browser settings, opt out of interest-based ads at optout.aboutads.info and your Meta and Google ad settings, and enable Global Privacy Control (GPC) in your browser — we treat a GPC signal as a request to opt out of sale/sharing for that browser.',
      ],
    },
    {
      id: 'marketing',
      heading: '6. Email and text messages',
      blocks: [
        'You can unsubscribe from marketing emails using the link in any email. If you opt in to text messages, message and data rates may apply and message frequency varies; reply STOP to cancel or HELP for help. Consent to marketing texts is not a condition of purchase. We will still send transactional messages about your orders.',
      ],
    },
    {
      id: 'rights',
      heading: '7. Your privacy rights',
      blocks: [
        'Depending on your state of residence (including California, Virginia, Colorado, Connecticut, Utah, Texas, Oregon, Florida and others), you may have the right to:',
        {
          list: [
            'Know and access the personal information we hold about you.',
            'Correct inaccurate personal information.',
            'Delete personal information, subject to legal exceptions (for example, order records we must keep for tax and warranty purposes).',
            'Obtain a portable copy of your information.',
            'Opt out of the sale or sharing of personal information, targeted advertising and profiling.',
            'Not be discriminated against for exercising these rights.',
          ],
        },
        `To exercise your rights, email ${c.privacyEmail} with the subject "Privacy Request". We will verify your identity using information associated with your orders and respond within the time required by law. You may use an authorized agent with your signed permission. If we deny your request, you may appeal by replying to our response with the subject "Appeal".`,
      ],
    },
    {
      id: 'retention',
      heading: '8. Data retention',
      blocks: [
        'We keep personal information as long as needed for the purposes described in this Policy — including for the duration of any warranty coverage on your purchase — and as required for tax, accounting, legal and fraud-prevention purposes. We then delete or de-identify it.',
      ],
    },
    {
      id: 'security',
      heading: '9. Security',
      blocks: [
        'We use reasonable administrative, technical and physical safeguards, and our checkout is encrypted. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.',
      ],
    },
    {
      id: 'children',
      heading: '10. Children',
      blocks: [
        'Our website is not directed to children under 16, and we do not knowingly collect personal information from children under 16. If you believe a child has provided us information, contact us and we will delete it.',
      ],
    },
    {
      id: 'location',
      heading: '11. Where we process data',
      blocks: [
        'We are based in the United States and process information in the United States. If you access our website from outside the U.S., you consent to the transfer and processing of your information in the U.S.',
      ],
    },
    {
      id: 'changes',
      heading: '12. Changes to this Policy',
      blocks: ['We may update this Policy from time to time. We will post the updated version on this page with a new "Last updated" date.'],
    },
    {
      id: 'contact',
      heading: '13. Contact',
      blocks: [`Privacy questions or requests: ${contact}.`],
    },
  ],
};
